/* eslint-disable no-underscore-dangle */
import { loadGoogleMaps } from './googleMapsLoader'

/**
 * 教学点地图 Google Maps 封装
 * 兼容 Leaflet 时代的调用风格：init / setMarkers / fitToMarkers / flyTo / on / latLngToPixel / destroy
 */
export default class GoogleMapAdapter {
  constructor({
    container,
    center,
    zoom,
    mapId,
    apiKey,
    language = 'en',
    region,
    mapOptions = {},
  }) {
    this.container = container
    this.options = {
      center,
      zoom,
      mapId,
      mapOptions,
    }
    this.apiKey = apiKey
    this.language = language
    this.region = region
    this.google = null
    this.map = null
    this.markers = new Map()
    this._listeners = []
    this._projectionOverlay = null
    this._renderMarker = null
  }

  async init() {
    const google = await loadGoogleMaps({
      apiKey: this.apiKey,
      libraries: ['marker'],
      language: this.language,
      region: this.region,
    })
    this.google = google

    if (!this.container) {
      throw new Error('GoogleMapAdapter: container is missing')
    }
    if (!google.maps || typeof google.maps.Map !== 'function') {
      throw new Error('Google Maps SDK loaded but google.maps.Map is unavailable')
    }

    this.map = new google.maps.Map(this.container, {
      center: this.options.center,
      zoom: this.options.zoom,
      mapId: this.options.mapId,
      disableDefaultUI: false,
      zoomControl: true,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      clickableIcons: false,
      ...this.options.mapOptions,
    })

    return this.map
  }

  /**
   * 渲染 markers
   * @param {Array} locations 必须含 { venueId, lat, lng }
   * @param {Object} opts
   * @param {*} opts.activeId
   * @param {(loc) => string|HTMLElement} opts.renderContent 返回 marker DOM 内容
   * @param {(loc) => void} opts.onClick
   */
  setMarkers(locations, { activeId, renderContent, onClick } = {}) {
    if (!this.map || !this.google) return
    const markerLib = this.google.maps.marker
    if (!markerLib || typeof markerLib.AdvancedMarkerElement !== 'function') {
      console.warn('[GoogleMapAdapter] AdvancedMarkerElement unavailable, marker library may not be loaded')
      return
    }
    const { AdvancedMarkerElement } = markerLib

    this.clearMarkers()

    if (renderContent) {
      this._renderMarker = renderContent
    }

    locations.forEach((loc) => {
      if (!Number.isFinite(loc.lat) || !Number.isFinite(loc.lng)) return

      const isActive = loc.venueId === activeId
      const wrap = document.createElement('div')
      wrap.className = `gmap-pin${isActive ? ' gmap-pin--active' : ''}`

      const html = renderContent ? renderContent(loc, isActive) : ''
      if (typeof html === 'string') {
        wrap.innerHTML = html
      } else if (html instanceof HTMLElement) {
        wrap.appendChild(html)
      }

      const marker = new AdvancedMarkerElement({
        map: this.map,
        position: { lat: loc.lat, lng: loc.lng },
        content: wrap,
        title: loc.name || '',
      })

      marker.addListener('gmp-click', () => {
        if (onClick) onClick(loc)
      })

      this.markers.set(loc.venueId, marker)
    })
  }

  /**
   * 自动框选所有 marker
   */
  fitToMarkers(locations, padding = 60) {
    if (!this.map || !this.google) return
    const valid = locations.filter((l) => Number.isFinite(l.lat) && Number.isFinite(l.lng))
    if (!valid.length) return

    if (valid.length === 1) {
      this.map.setCenter({ lat: valid[0].lat, lng: valid[0].lng })
      return
    }
    const bounds = new this.google.maps.LatLngBounds()
    valid.forEach((l) => bounds.extend({ lat: l.lat, lng: l.lng }))
    this.map.fitBounds(bounds, padding)
  }

  /**
   * 计算地图中心：使目标坐标落在视口纵向 focusAtViewportRatio 处（0=顶，1=底）
   * 一次 panTo 即可到位，避免先居中再 panBy 的两段动画
   */
  _computeFocusCenter({ lat, lng }, focusAtViewportRatio) {
    if (!this.map || !this.google || !this.container || !Number.isFinite(focusAtViewportRatio)) {
      return { lat, lng }
    }
    const projection = this.map.getProjection()
    if (!projection) return { lat, lng }

    const zoom = this.map.getZoom()
    const scale = 2 ** zoom
    const height = this.container.offsetHeight
    const target = new this.google.maps.LatLng(lat, lng)
    const worldPoint = projection.fromLatLngToPoint(target)
    const pixelOffsetY = (height * (0.5 - focusAtViewportRatio)) / scale
    const newWorldPoint = new this.google.maps.Point(
      worldPoint.x,
      worldPoint.y + pixelOffsetY,
    )
    const newCenter = projection.fromPointToLatLng(newWorldPoint)
    return { lat: newCenter.lat(), lng: newCenter.lng() }
  }

  /**
   * 平移到指定坐标；可选将坐标落在视口纵向比例处（0=顶部，1=底部，默认中心 0.5），为上方浮层留出空间
   * @param {{ lat: number, lng: number }} coords
   * @param {number} [zoom]
   * @param {{ focusAtViewportRatio?: number, onSettled?: () => void }} [options]
   */
  flyTo({ lat, lng }, zoom, options = {}) {
    if (!this.map || !Number.isFinite(lat) || !Number.isFinite(lng)) return
    const { focusAtViewportRatio, onSettled } = options

    if (Number.isFinite(zoom)) {
      this.map.setZoom(zoom)
    }

    const center = Number.isFinite(focusAtViewportRatio)
      ? this._computeFocusCenter({ lat, lng }, focusAtViewportRatio)
      : { lat, lng }

    if (onSettled) {
      this.google.maps.event.addListenerOnce(this.map, 'idle', onSettled)
    }

    this.map.panTo(center)
  }

  /**
   * 容器尺寸变化后调用（如抽屉打开后）
   */
  resize() {
    if (!this.map || !this.google) return
    this.google.maps.event.trigger(this.map, 'resize')
  }

  /**
   * 监听地图事件，统一管理 listener 便于销毁
   */
  on(event, handler) {
    if (!this.map) return null
    const listener = this.map.addListener(event, handler)
    this._listeners.push(listener)
    return listener
  }

  /**
   * 挂载一个隐形 OverlayView，用来做坐标 -> 像素的换算（自定义浮层 popup 用）
   * @returns {Promise<void>} resolve 时 projection 已可用
   */
  attachProjectionHelper() {
    if (!this.map || !this.google) return Promise.reject(new Error('map is not ready'))
    if (this._projectionOverlay) return Promise.resolve()

    return new Promise((resolve) => {
      const overlay = new this.google.maps.OverlayView()
      overlay.onAdd = () => {
        resolve()
      }
      overlay.draw = () => {}
      overlay.onRemove = () => {}
      overlay.setMap(this.map)
      this._projectionOverlay = overlay
    })
  }

  /**
   * 经纬度 -> 容器像素，OverlayView 未就绪时返回 null
   */
  latLngToPixel({ lat, lng }) {
    if (!this._projectionOverlay || !this.google) return null
    const projection = this._projectionOverlay.getProjection()
    if (!projection) return null
    const point = projection.fromLatLngToContainerPixel(
      new this.google.maps.LatLng(lat, lng),
    )
    return point ? { x: point.x, y: point.y } : null
  }

  clearMarkers() {
    this.markers.forEach((marker) => {
      // eslint-disable-next-line no-param-reassign
      marker.map = null
    })
    this.markers.clear()
  }

  destroy() {
    this.clearMarkers()
    this._listeners.forEach((l) => {
      if (l && typeof l.remove === 'function') l.remove()
    })
    this._listeners = []

    if (this._projectionOverlay) {
      this._projectionOverlay.setMap(null)
      this._projectionOverlay = null
    }

    if (this.map && this.container) {
      // Google Maps 没有原生 dispose API，移除事件监听 + 清空容器引用即可
      this.google.maps.event.clearInstanceListeners(this.map)
    }
    this.map = null
    this.google = null
  }
}
